// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const SUBMENU_OVERLAP = 1;
export function useContextMenu({ menuRef, menuProps, openedMenuRefs }) {
    const [subMenuProps, setSubMenuProps] = useState(null);
    // Reset submenu when this menu's items change.
    // Happens when a parent switches to a different item's subitems —
    // React reuses the component (same type & position) but the old nested submenu state must be cleared.
    const [prevItems, setPrevItems] = useState(menuProps.items);
    if (prevItems !== menuProps.items) {
        setPrevItems(menuProps.items);
        setSubMenuProps(null);
    }
    // Move root menu behind its first submenu (once).
    // Portal order is [Root, Level2, Level3 …]. After appendChild: [Level2, Root, Level3 …].
    // Root stays above Level2 (overlap zone) but below Level3+.
    // Runs only on the null → non-null transition so re-hovering the same item doesn't push Root past deeper levels.
    const wasSubMenuOpen = useRef(false);
    useLayoutEffect(() => {
        var _a;
        if (!menuProps.hasParent) {
            if (subMenuProps !== null && !wasSubMenuOpen.current) {
                wasSubMenuOpen.current = true;
                const node = menuRef.current;
                (_a = node === null || node === void 0 ? void 0 : node.parentElement) === null || _a === void 0 ? void 0 : _a.appendChild(node);
            }
            if (subMenuProps === null) {
                wasSubMenuOpen.current = false;
            }
        }
    }, [subMenuProps, menuRef, menuProps.hasParent]);
    // Register this menu's ref so every ancestor Popover sees it in excludeRefs.
    useEffect(() => {
        const refs = openedMenuRefs;
        refs.push(menuRef);
        return () => {
            const idx = refs.indexOf(menuRef);
            if (idx !== -1) {
                refs.splice(idx, 1);
            }
        };
    }, [openedMenuRefs, menuRef]);
    // Adjust menu position so it stays within the viewport.
    // Runs after every render (before paint) to correct position even if React resets inline styles.
    useLayoutEffect(() => {
        const el = menuRef.current;
        if (el === null)
            return;
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (rect.bottom > vh) {
            el.style.top = `${Math.max(0, vh - rect.height)}px`;
        }
        if (rect.right > vw) {
            el.style.left = `${Math.max(0, vw - rect.width)}px`;
        }
        if (rect.left < 0) {
            el.style.left = '0px';
        }
    });
    const onItemPointerEnter = useCallback((item, element) => {
        var _a, _b, _c;
        if (isValidArray(item.subitems)) {
            const itemRect = element.getBoundingClientRect();
            const menuRect = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const vw = window.innerWidth;
            // Estimate submenu width using parent menu width (similar items structure).
            const estimatedWidth = (_b = menuRect === null || menuRect === void 0 ? void 0 : menuRect.width) !== null && _b !== void 0 ? _b : 200;
            // Prefer opening to the right; if not enough space, open to the left of the parent menu.
            let x;
            const rightX = itemRect.right - SUBMENU_OVERLAP;
            const leftX = ((_c = menuRect === null || menuRect === void 0 ? void 0 : menuRect.left) !== null && _c !== void 0 ? _c : 0) - estimatedWidth + SUBMENU_OVERLAP;
            if (rightX + estimatedWidth <= vw) {
                x = rightX;
            }
            else if (leftX >= 0) {
                x = leftX;
            }
            else {
                x = Math.max(0, vw - estimatedWidth);
            }
            setSubMenuProps({
                items: item.subitems,
                position: { x, y: itemRect.top },
                onClose: menuProps.onClose,
                minWidth: menuProps.minWidth,
                maxWidth: menuProps.maxWidth,
                hasParent: true,
                openedMenuRefs
            });
        }
        else {
            setSubMenuProps(null);
        }
    }, [menuRef]);
    const closeSubmenu = useCallback(() => {
        setSubMenuProps(null);
    }, []);
    const hasLeftOffset = menuProps.items.some((item) => item.type === 'item' && (item.icon !== undefined || item.checked === true));
    const hasRightOffset = menuProps.items.some((item) => item.type === 'item' && isValidArray(item.subitems));
    return {
        subMenuProps,
        hasLeftOffset,
        hasRightOffset,
        onItemPointerEnter,
        closeSubmenu
    };
}
